import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Sparkles,
    ChefHat,
    Lightbulb,
    Calendar,
    Wand2,
    Loader2,
    DollarSign,
    Clock,
    Users,
    ArrowRight,
    Lock,
    Crown,
    Plus,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export const AIPage = () => {
    const { user, isPremium } = useAuth();
    const navigate = useNavigate();
    
    const [activeTab, setActiveTab] = useState('recipe');
    const [loading, setLoading] = useState(false);
    
    // Recipe suggestion state
    const [recipePrompt, setRecipePrompt] = useState('');
    const [recipeBudget, setRecipeBudget] = useState('');
    const [recipeServings, setRecipeServings] = useState(4);
    const [suggestedRecipe, setSuggestedRecipe] = useState(null);
    
    // Tip state
    const [suggestedTip, setSuggestedTip] = useState(null);
    
    // Meal plan state
    const [mealPlanBudget, setMealPlanBudget] = useState(50);
    const [mealPlanFamily, setMealPlanFamily] = useState(4);
    const [mealPlanPreferences, setMealPlanPreferences] = useState('');
    const [suggestedMealPlan, setSuggestedMealPlan] = useState(null);

    const handleSuggestRecipe = async () => {
        if (!recipePrompt.trim()) {
            toast.error('Décrivez ce que vous voulez cuisiner');
            return;
        }
        
        setLoading(true);
        setSuggestedRecipe(null);
        
        try {
            const response = await api.suggestRecipe({
                prompt: recipePrompt,
                budget: recipeBudget ? parseFloat(recipeBudget) : null,
                servings: recipeServings
            });
            setSuggestedRecipe(response.data);
            toast.success('Recette générée!');
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestTip = async () => {
        setLoading(true);
        setSuggestedTip(null);
        
        try {
            const response = await api.suggestTip();
            setSuggestedTip(response.data.tip);
            toast.success('Astuce générée!');
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestMealPlan = async () => {
        setLoading(true);
        setSuggestedMealPlan(null);
        
        try {
            const response = await api.suggestMealPlan({
                budget: mealPlanBudget,
                family_size: mealPlanFamily,
                preferences: mealPlanPreferences
            });
            setSuggestedMealPlan(response.data);
            toast.success('Plan de repas généré!');
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
        } finally {
            setLoading(false);
        }
    };

    // Non-premium users see a CTA
    if (!isPremium) {
        return (
            <div className="min-h-screen py-12 md:py-24">
                <div className="max-w-2xl mx-auto px-4 text-center">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock className="w-10 h-10 text-primary" />
                    </div>
                    <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
                        Fonctionnalité Premium
                    </h1>
                    <p className="text-muted-foreground text-lg mb-8">
                        Les suggestions IA sont réservées aux membres Premium. 
                        Obtenez des recettes personnalisées, des astuces uniques et des plans de repas sur mesure!
                    </p>
                    
                    <Card className="rounded-2xl mb-8">
                        <CardContent className="p-8">
                            <h3 className="font-heading font-bold text-xl mb-4 flex items-center justify-center gap-2">
                                <Sparkles className="w-5 h-5 text-primary" />
                                Ce que vous débloquez
                            </h3>
                            <ul className="space-y-3 text-left max-w-md mx-auto">
                                <li className="flex items-center gap-3">
                                    <ChefHat className="w-5 h-5 text-secondary" />
                                    <span>Suggestions de recettes personnalisées</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Lightbulb className="w-5 h-5 text-secondary" />
                                    <span>Astuces économiques uniques</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Calendar className="w-5 h-5 text-secondary" />
                                    <span>Plans de repas hebdomadaires</span>
                                </li>
                                <li className="flex items-center gap-3">
                                    <Wand2 className="w-5 h-5 text-secondary" />
                                    <span>Amélioration de vos recettes</span>
                                </li>
                            </ul>
                        </CardContent>
                    </Card>
                    
                    <Button
                        onClick={() => navigate('/subscription')}
                        className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-bold"
                    >
                        <Crown className="w-5 h-5 mr-2" />
                        Devenir Premium
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 md:py-12" data-testid="ai-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary font-medium text-sm mb-4">
                        <Sparkles className="w-4 h-4" />
                        Propulsé par GPT-5.2
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
                        Assistant Culinaire IA
                    </h1>
                    <p className="text-muted-foreground text-lg">
                        Votre chef personnel intelligent pour des repas économiques et savoureux
                    </p>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                    <TabsList className="grid grid-cols-3 w-full max-w-lg mx-auto">
                        <TabsTrigger value="recipe" className="flex items-center gap-2">
                            <ChefHat className="w-4 h-4" />
                            Recette
                        </TabsTrigger>
                        <TabsTrigger value="tip" className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Astuce
                        </TabsTrigger>
                        <TabsTrigger value="mealplan" className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Plan Repas
                        </TabsTrigger>
                    </TabsList>

                    {/* Recipe Suggestion Tab */}
                    <TabsContent value="recipe">
                        <Card className="rounded-2xl">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ChefHat className="w-5 h-5 text-primary" />
                                    Suggestion de Recette
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div>
                                    <Label>Que voulez-vous cuisiner?</Label>
                                    <Textarea
                                        placeholder="Ex: Un plat réconfortant avec du poulet, quelque chose de rapide pour un soir de semaine, une recette végétarienne..."
                                        value={recipePrompt}
                                        onChange={(e) => setRecipePrompt(e.target.value)}
                                        rows={3}
                                        className="mt-2"
                                    />
                                </div>
                                
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="flex items-center gap-2">
                                            <DollarSign className="w-4 h-4" />
                                            Budget max par portion
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.50"
                                            placeholder="Ex: 3.00"
                                            value={recipeBudget}
                                            onChange={(e) => setRecipeBudget(e.target.value)}
                                            className="mt-2"
                                        />
                                    </div>
                                    <div>
                                        <Label className="flex items-center gap-2">
                                            <Users className="w-4 h-4" />
                                            Nombre de portions
                                        </Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="12"
                                            value={recipeServings}
                                            onChange={(e) => setRecipeServings(parseInt(e.target.value))}
                                            className="mt-2"
                                        />
                                    </div>
                                </div>
                                
                                <Button
                                    onClick={handleSuggestRecipe}
                                    disabled={loading}
                                    className="w-full bg-primary text-white hover:bg-primary/90 rounded-xl py-6"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-5 h-5 mr-2" />
                                            Générer une recette
                                        </>
                                    )}
                                </Button>

                                {/* Recipe Result */}
                                {suggestedRecipe && (
                                    <div className="mt-8 p-6 bg-muted/50 rounded-xl">
                                        <h3 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                                            <Sparkles className="w-5 h-5 text-primary" />
                                            Recette suggérée
                                        </h3>
                                        
                                        {suggestedRecipe.parsed && suggestedRecipe.recipe ? (
                                            <div className="space-y-4">
                                                <div>
                                                    <h4 className="font-bold text-2xl text-foreground">{suggestedRecipe.recipe.title}</h4>
                                                    <p className="text-muted-foreground mt-1">{suggestedRecipe.recipe.description}</p>
                                                </div>
                                                
                                                <div className="flex flex-wrap gap-3">
                                                    <Badge variant="outline" className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {(suggestedRecipe.recipe.prep_time || 0) + (suggestedRecipe.recipe.cook_time || 0)} min
                                                    </Badge>
                                                    <Badge variant="outline" className="flex items-center gap-1">
                                                        <DollarSign className="w-3 h-3" />
                                                        {suggestedRecipe.recipe.cost_per_serving?.toFixed(2)}$/portion
                                                    </Badge>
                                                    <Badge variant="outline" className="flex items-center gap-1">
                                                        <Users className="w-3 h-3" />
                                                        {suggestedRecipe.recipe.servings} portions
                                                    </Badge>
                                                    <Badge variant="secondary">{suggestedRecipe.recipe.difficulty}</Badge>
                                                </div>
                                                
                                                {suggestedRecipe.recipe.ingredients && (
                                                    <div>
                                                        <h5 className="font-semibold mb-2">Ingrédients:</h5>
                                                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                                                            {suggestedRecipe.recipe.ingredients.map((ing, idx) => (
                                                                <li key={idx}>
                                                                    {ing.quantity} {ing.unit} {ing.name}
                                                                    {ing.estimated_cost && <span className="text-primary ml-2">({ing.estimated_cost}$)</span>}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                
                                                {suggestedRecipe.recipe.steps && (
                                                    <div>
                                                        <h5 className="font-semibold mb-2">Étapes:</h5>
                                                        <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
                                                            {suggestedRecipe.recipe.steps.map((step, idx) => (
                                                                <li key={idx}>{step}</li>
                                                            ))}
                                                        </ol>
                                                    </div>
                                                )}
                                                
                                                {suggestedRecipe.recipe.tips && (
                                                    <div className="bg-accent/20 rounded-lg p-4">
                                                        <h5 className="font-semibold mb-1 flex items-center gap-2">
                                                            <Lightbulb className="w-4 h-4" />
                                                            Astuce du chef
                                                        </h5>
                                                        <p className="text-sm text-muted-foreground">{suggestedRecipe.recipe.tips}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="prose prose-sm max-w-none">
                                                <pre className="whitespace-pre-wrap text-sm bg-background p-4 rounded-lg">
                                                    {suggestedRecipe.suggestion}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tip Tab */}
                    <TabsContent value="tip">
                        <Card className="rounded-2xl">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                                    Astuce Économique
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <p className="text-muted-foreground">
                                    Découvrez une astuce unique pour économiser sur vos courses ou en cuisine. 
                                    Chaque astuce est générée spécialement pour vous!
                                </p>
                                
                                <Button
                                    onClick={handleSuggestTip}
                                    disabled={loading}
                                    className="w-full bg-yellow-500 text-white hover:bg-yellow-600 rounded-xl py-6"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw className="w-5 h-5 mr-2" />
                                            {suggestedTip ? 'Nouvelle astuce' : 'Générer une astuce'}
                                        </>
                                    )}
                                </Button>

                                {suggestedTip && (
                                    <div className="mt-6 p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <Lightbulb className="w-5 h-5 text-yellow-600" />
                                            </div>
                                            <div className="prose prose-sm max-w-none">
                                                <div className="whitespace-pre-wrap">{suggestedTip}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Meal Plan Tab */}
                    <TabsContent value="mealplan">
                        <Card className="rounded-2xl">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-green-500" />
                                    Plan de Repas Hebdomadaire
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="flex items-center gap-2">
                                            <DollarSign className="w-4 h-4" />
                                            Budget total (5 soupers)
                                        </Label>
                                        <Input
                                            type="number"
                                            min="20"
                                            max="200"
                                            value={mealPlanBudget}
                                            onChange={(e) => setMealPlanBudget(parseInt(e.target.value))}
                                            className="mt-2"
                                        />
                                    </div>
                                    <div>
                                        <Label className="flex items-center gap-2">
                                            <Users className="w-4 h-4" />
                                            Taille de la famille
                                        </Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="10"
                                            value={mealPlanFamily}
                                            onChange={(e) => setMealPlanFamily(parseInt(e.target.value))}
                                            className="mt-2"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <Label>Préférences ou restrictions (optionnel)</Label>
                                    <Textarea
                                        placeholder="Ex: végétarien, sans gluten, les enfants n'aiment pas les champignons..."
                                        value={mealPlanPreferences}
                                        onChange={(e) => setMealPlanPreferences(e.target.value)}
                                        rows={2}
                                        className="mt-2"
                                    />
                                </div>
                                
                                <Button
                                    onClick={handleSuggestMealPlan}
                                    disabled={loading}
                                    className="w-full bg-green-500 text-white hover:bg-green-600 rounded-xl py-6"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Génération en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Calendar className="w-5 h-5 mr-2" />
                                            Générer mon plan de repas
                                        </>
                                    )}
                                </Button>

                                {/* Meal Plan Result */}
                                {suggestedMealPlan && (
                                    <div className="mt-8 space-y-6">
                                        {suggestedMealPlan.parsed && suggestedMealPlan.plan ? (
                                            <>
                                                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                                                    <span className="font-semibold">Budget total estimé:</span>
                                                    <span className="text-2xl font-bold text-green-600">
                                                        {suggestedMealPlan.plan.total_budget?.toFixed(2)}$
                                                    </span>
                                                </div>
                                                
                                                <div>
                                                    <h4 className="font-semibold mb-4">Vos 5 soupers:</h4>
                                                    <div className="space-y-3">
                                                        {suggestedMealPlan.plan.meals?.map((meal, idx) => (
                                                            <div key={idx} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                                                                <div className="w-20 font-bold text-primary">{meal.day}</div>
                                                                <div className="flex-1">
                                                                    <h5 className="font-semibold">{meal.title}</h5>
                                                                    <p className="text-sm text-muted-foreground">{meal.description}</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <div className="font-bold text-primary">{meal.cost?.toFixed(2)}$</div>
                                                                    <div className="text-xs text-muted-foreground">{meal.prep_time} min</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                {suggestedMealPlan.plan.grocery_list && (
                                                    <div>
                                                        <h4 className="font-semibold mb-4">Liste d'épicerie:</h4>
                                                        <div className="grid sm:grid-cols-2 gap-2">
                                                            {suggestedMealPlan.plan.grocery_list.map((item, idx) => (
                                                                <div key={idx} className="flex justify-between p-2 bg-muted/30 rounded-lg">
                                                                    <span>{item.quantity} {item.item}</span>
                                                                    <span className="text-primary">{item.estimated_cost?.toFixed(2)}$</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {suggestedMealPlan.plan.tips && (
                                                    <div className="bg-green-50 rounded-xl p-4">
                                                        <h5 className="font-semibold mb-2 flex items-center gap-2">
                                                            <Lightbulb className="w-4 h-4" />
                                                            Conseils
                                                        </h5>
                                                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                                                            {suggestedMealPlan.plan.tips.map((tip, idx) => (
                                                                <li key={idx}>{tip}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="prose prose-sm max-w-none">
                                                <pre className="whitespace-pre-wrap text-sm bg-background p-4 rounded-lg">
                                                    {suggestedMealPlan.raw}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default AIPage;
